import React, { useState } from 'react';

import { InputAdornment, TextField } from '@mui/material';

interface Props {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  /** Smallest increment the spinner steps by, and the precision the field accepts. */
  step?: number;
  min?: number;
  /** Unit rendered inside the field, e.g. `¢`. */
  unit?: React.ReactNode;
  inputLabel?: string;
}

/**
 * A numeric text field that can be cleared while typing.
 *
 * A plain controlled `<TextField type="number" value={someNumber} onChange={(e) =>
 * onChange(Number(e.target.value))} />` cannot be emptied. Deleting the last character fires
 * `onChange` with `''`, `Number('')` is `0`, and that `0` is rendered straight back into the box —
 * so a field showing `0` can never be cleared to type a different number over it, only prefixed or
 * suffixed. It also makes `.1` the only way to reach `0.1`, since the leading `0` is immovable.
 *
 * This keeps the raw text being typed in local state, so the box can sit empty or hold an
 * in-progress value like `.` or `1.`, and reports upward only what actually parses. The draft is
 * dropped on blur, at which point the committed number is displayed again — so leaving the field
 * empty reverts to the last valid value rather than silently writing a zero.
 */
export default function NumericInput({ value, onChange, disabled, step, min, unit, inputLabel }: Props) {
  // Non-null only while the user is mid-edit; null means "show the committed value".
  const [draft, setDraft] = useState<string | null>(null);

  const handleChange = (raw: string) => {
    setDraft(raw);

    // An empty or partial entry (`''`, `.`, `-`) is a legitimate waypoint while typing, not a
    // value — leave the model on its last good number until something parseable arrives.
    if (raw === '') {
      return;
    }
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) {
      onChange(parsed);
    }
  };

  return (
    <TextField
      type="number"
      size="small"
      value={draft ?? String(value)}
      onChange={(event) => handleChange(event.target.value)}
      onBlur={() => setDraft(null)}
      disabled={disabled}
      slotProps={{
        htmlInput: { step, min, 'aria-label': inputLabel },
        ...(unit && { input: { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> } }),
      }}
    />
  );
}
